import { ApolloError } from "@apollo/client";
import {
    IClusterDetails,
    IClusterMachineCenter,
    IClusterStore,
    ICompressedHMIData,
    IClusterMachineCenterResponse,
    IClusterFailureType,
} from "./ClusterCachedDataContextInterfaces";
import {
    FAILURE_TYPES_CODE_EXCEPTIONS,
    LocalStorageCachedDataKeysCluster,
    LocalStorageCachedDataKeysHMI,
} from "constants/constants";
import { getEditionsPendingHMIStore, getLocalStorageItem, setLocalStorageItem } from "utilities/storageUtilities";
import { compress } from "lz-string";
import {
    IDefaultUptimeValues,
    IEventsEditionsPending,
    IMQTTBrokerSettings,
} from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { LocalserverSetupMethodsEntity } from "mqtt/entities/LocalserverSetupMethods/LocalserverSetupMethodsEntity";
import { getClusterData } from "../../api/queries/clusterDataAPI";
import { API } from "../../api/API";
import { getLastTwoShiftsMachineCentersInfoByCluster } from "api/queries/ShiftsAPI";
import { ILastTwoShiftsEventsByMachineCenter } from "entities/LiveDowntimeReportEntity/LiveDowntimeReportEntityInterfaces";
import { validateLocalserverDataFreshness } from "utilities/utilities";

export const setCachedData = (setStore: (value: React.SetStateAction<IClusterStore>) => void, error?: ApolloError) => {
    try {
        let cachedStore = {};
        const clusterDetailsLocalStorage = localStorage.getItem(LocalStorageCachedDataKeysCluster.CLUSTER_DETAILS);
        const machineCentersClusterLocalStorage = getLocalStorageItem(
            LocalStorageCachedDataKeysCluster.MACHINE_CENTERS_CLUSTER,
        );
        const dataLastUpdatedLocalStorage = localStorage.getItem(LocalStorageCachedDataKeysHMI.DATA_LAST_UPDATED);
        const mqttBrokerSettingsLocalStorage = localStorage.getItem(LocalStorageCachedDataKeysHMI.MQTT_BROKER_SETTINGS);
        const defaultUptimeValuesLocalStorage = localStorage.getItem(
            LocalStorageCachedDataKeysHMI.DEFAULT_UPTIME_VALUES,
        );

        if (
            clusterDetailsLocalStorage ||
            machineCentersClusterLocalStorage ||
            dataLastUpdatedLocalStorage ||
            mqttBrokerSettingsLocalStorage ||
            defaultUptimeValuesLocalStorage
        ) {
            if (clusterDetailsLocalStorage) {
                const clusterDetails: IClusterDetails = JSON.parse(clusterDetailsLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    clusterDetails: clusterDetails,
                    apolloQueryError: undefined,
                };
            }
            if (machineCentersClusterLocalStorage) {
                const machineCentersCluster: IClusterMachineCenter[] = JSON.parse(machineCentersClusterLocalStorage!);

                const hmiCompressedData = machineCentersCluster.reduce(
                    (acc: ICompressedHMIData, machineCenterCluster) => {
                        const {
                            last_two_shifts_event_reports,
                            non_location_specific_failure_codes,
                            failure_types,
                            ...machineCenter
                        } = machineCenterCluster;
                        const machineCenterInfoCompressed = compress(JSON.stringify(machineCenter));
                        const lastTwoShiftsEventReportsCompressed = compress(
                            JSON.stringify(last_two_shifts_event_reports),
                        );

                        acc[machineCenter.machine_center_id] = {
                            machineCenterInfo: machineCenterInfoCompressed,
                            lastTwoShiftsEventReports: lastTwoShiftsEventReportsCompressed,
                        };
                        return acc;
                    },
                    {},
                );

                cachedStore = {
                    ...cachedStore,
                    machineCentersCluster: machineCentersCluster,
                    apolloQueryError: undefined,
                    compressedHMIData: hmiCompressedData,
                };
            }
            if (dataLastUpdatedLocalStorage) {
                const lastUpdated: Date = JSON.parse(dataLastUpdatedLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    lastUpdated: lastUpdated,
                    apolloQueryError: undefined,
                };
            }
            if (mqttBrokerSettingsLocalStorage) {
                const mqttBrokerSettings: IMQTTBrokerSettings[] = JSON.parse(mqttBrokerSettingsLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    mqttBrokerSettings,
                    apolloQueryError: undefined,
                };
            }
            if (defaultUptimeValuesLocalStorage) {
                const defaultUptimeValues: IDefaultUptimeValues = JSON.parse(defaultUptimeValuesLocalStorage!);
                cachedStore = {
                    ...cachedStore,
                    defaultUptimeValues,
                    apolloQueryError: undefined,
                };
            }
        }
        setStore((prevStore) => ({
            ...prevStore,
            ...cachedStore,
            ...(error && { apolloQueryError: error }),
        }));
    } catch (error) {
        throw error;
    }
};

export const compressHMIData = (machineCentersCluster: IClusterMachineCenter[]) => {
    try {
        const hmiCompressedData = machineCentersCluster.reduce((acc: ICompressedHMIData, machineCenterCluster) => {
            const {
                last_two_shifts_event_reports,
                non_location_specific_failure_codes,
                failure_types,
                ...machineCenter
            } = machineCenterCluster;
            const machineCenterInfoCompressed = compress(JSON.stringify(machineCenter));
            const lastTwoShiftsEventReportsCompressed = compress(JSON.stringify(last_two_shifts_event_reports));

            acc[machineCenter.machine_center_id] = {
                machineCenterInfo: machineCenterInfoCompressed,
                lastTwoShiftsEventReports: lastTwoShiftsEventReportsCompressed,
            };
            return acc;
        }, {});

        return hmiCompressedData;
    } catch {
        return {};
    }
};

export const parseClusterData = (
    machineCentersCluster: IClusterMachineCenter[],
    clusterDetails: IClusterDetails,
    lastUpdated: Date,
    mqttBrokerSettings: IMQTTBrokerSettings[],
    defaultUptimeValues: IDefaultUptimeValues,
    setStore: (value: React.SetStateAction<IClusterStore>) => void,
) => {
    try {
        const hmiCompressedData = compressHMIData(machineCentersCluster);
        const editionsPendingLocalStorage = getEditionsPendingHMIStore() || {};

        setStore((prev) => {
            const editionsPendingUpdate: IEventsEditionsPending = {};

            for (const machineCenter of prev.machineCentersCluster) {
                const currentShiftDowntimeEvent =
                    machineCenter?.last_two_shifts_event_reports?.currentShiftEventWithHours || [];
                const previousShiftDowntimeEvent =
                    machineCenter?.last_two_shifts_event_reports?.previousShiftEventWithHours || [];
                const eventReports = [...currentShiftDowntimeEvent, ...previousShiftDowntimeEvent];
                for (const event of eventReports) {
                    const recenetlyUpdateDate = editionsPendingLocalStorage[event.event_report_id]?.lastUpdateDate;
                    if (!!recenetlyUpdateDate) {
                        const isNotUpdated = !event.updated_at || recenetlyUpdateDate >= event?.updated_at;
                        if (isNotUpdated)
                            editionsPendingUpdate[event.event_report_id] = {
                                ...editionsPendingLocalStorage[event.event_report_id],
                            };
                    }
                }
            }

            setLocalStorageItem(LocalStorageCachedDataKeysHMI.EDITIONS_PENDING, JSON.stringify(editionsPendingUpdate));

            return {
                ...prev,
                clusterDetails: clusterDetails,
                machineCentersCluster: machineCentersCluster,
                lastUpdated: lastUpdated,
                compressedHMIData: hmiCompressedData,
                mqttBrokerSettings,
                defaultUptimeValues,
            };
        });
        localStorage.setItem(LocalStorageCachedDataKeysCluster.CLUSTER_DETAILS, JSON.stringify(clusterDetails));
        setLocalStorageItem(
            LocalStorageCachedDataKeysCluster.MACHINE_CENTERS_CLUSTER,
            JSON.stringify(machineCentersCluster),
        );
        localStorage.setItem(LocalStorageCachedDataKeysHMI.DATA_LAST_UPDATED, JSON.stringify(lastUpdated));
        localStorage.setItem(LocalStorageCachedDataKeysHMI.MQTT_BROKER_SETTINGS, JSON.stringify(mqttBrokerSettings));
        localStorage.setItem(LocalStorageCachedDataKeysHMI.DEFAULT_UPTIME_VALUES, JSON.stringify(defaultUptimeValues));
    } catch (error) {
        throw new Error(`Error while parsing data and setting data: ${error}`);
    }
};

export const getInfoFromLocalserver = async (
    clusterId: number,
    setStore: (value: React.SetStateAction<IClusterStore>) => void,
    mqttBrokerSettings: IMQTTBrokerSettings,
) => {
    try {
        const response = await LocalserverSetupMethodsEntity.getClusterInfo(clusterId, mqttBrokerSettings);

        if (response?.clusterInfo) {
            const cluster = response?.clusterInfo;
            const machineCenters = response?.clusterInfo.machine_centers;
            const machineCentersCluster: IClusterMachineCenter[] = machineCenters.map((machineCenter) => {
                const {
                    failure_types,
                    last_two_shifts_info,
                    non_location_specific_machine_center_failure_codes,
                    ...machineCenterInfo
                } = machineCenter;

                return {
                    ...machineCenterInfo,
                    non_location_specific_failure_codes: non_location_specific_machine_center_failure_codes,
                    failure_types: failure_types,
                    last_two_shifts_event_reports: last_two_shifts_info,
                };
            });

            validateLocalserverDataFreshness(response?.dataUpdatedTime);

            const lastUpdated: Date | undefined = response?.dataUpdatedTime || new Date();
            const mqttBrokerSettings = machineCenters?.[0]?.production_line?.mill?.mqtt_broker_settings;
            const defaultUptimeValues: IDefaultUptimeValues = {
                no_operator_id: machineCenters?.[0].no_operator_id,
                not_selected_type_id: machineCenters?.[0]?.failure_types?.find(
                    (ft) => ft.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
                )?.failure_type_id,
            };
            const clusterDetails: IClusterDetails = {
                mill_id: machineCenters?.[0]?.production_line?.mill?.mill_id,
                cluster_id: cluster?.cluster_id,
                name: cluster?.name,
                cluster_code: cluster?.cluster_code,
                user_cluster: cluster?.user_clusters,
                dedicated_localserver: cluster?.machine_centers?.[0]?.production_line?.mill?.dedicated_localserver,
            };

            parseClusterData(
                machineCentersCluster,
                clusterDetails,
                lastUpdated,
                mqttBrokerSettings,
                defaultUptimeValues,
                setStore,
            );
        } else {
            throw new Error(`No machine center info received from localserver: ${response}`);
        }
    } catch (error) {
        throw new Error(`Error while getting cluster info from localserver: ${error}`);
    }
};

export const getInfoFromCloudserver = async (
    clusterId: number,
    setStore: (value: React.SetStateAction<IClusterStore>) => void,
) => {
    try {
        const apolloClient = new API().client;

        const [lastTwoShiftsEventsResponse, apolloResponse] = await Promise.all([
            getLastTwoShiftsMachineCentersInfoByCluster(clusterId),
            apolloClient.query({
                query: getClusterData,
                variables: {
                    cluster_id: clusterId,
                },
            }),
        ]);

        const lastTwoShiftsEventsByCluster = lastTwoShiftsEventsResponse;
        const { error, data } = apolloResponse;

        if (data?.cluster && !error) {
            const machineCenterResponse: IClusterMachineCenterResponse[] = data.cluster[0].machine_centers;
            const failureTypesResponse: IClusterFailureType[] = data?.failure_type || [];
            const machineCentersCluster: IClusterMachineCenter[] = machineCenterResponse.map(
                (machineCenter: IClusterMachineCenterResponse) => {
                    //Deep cloned array to disassociate from original object
                    let failureTypesArray: IClusterFailureType[] = failureTypesResponse.reduce(
                        (acc: IClusterFailureType[], ft) => {
                            if (!ft.is_location_specific) {
                                const nonLocationFailureCodes = ft.failure_codes.filter(
                                    (fc) =>
                                        !fc.corporate_id ||
                                        machineCenter.non_location_specific_failure_codes.some(
                                            (nonLocationFC) => nonLocationFC.failure_code_id === fc.failure_code_id,
                                        ),
                                );
                                if (nonLocationFailureCodes.length) {
                                    const failureType = {
                                        ...ft,
                                        failure_codes: nonLocationFailureCodes,
                                    };
                                    acc.push(failureType);
                                }
                            } else {
                                const locationFailureCodes = ft.failure_codes.filter((fc) =>
                                    machineCenter.areas.some((area) =>
                                        area.functional_locations.some((fl) =>
                                            fl.location_specific_functional_location_failure_codes?.some(
                                                (locationFC) => locationFC.failure_code_id === fc.failure_code_id,
                                            ),
                                        ),
                                    ),
                                );
                                if (locationFailureCodes.length) {
                                    const failureType = { ...ft, failure_codes: locationFailureCodes };
                                    acc.push(failureType);
                                }
                            }
                            return acc;
                        },
                        [],
                    );

                    const lastTwoShiftsEvents = lastTwoShiftsEventsByCluster?.find(
                        (lastTwoShiftsEvent) => lastTwoShiftsEvent[machineCenter.machine_center_id],
                    ) as ILastTwoShiftsEventsByMachineCenter;
                    return {
                        ...machineCenter,
                        failure_types: failureTypesArray,
                        last_two_shifts_event_reports: lastTwoShiftsEvents?.[machineCenter.machine_center_id],
                    };
                },
            );

            const lastUpdated: Date | undefined = new Date();
            const mqttBrokerSettings = data.cluster[0].mill.mqtt_broker_settings;
            const defaultUptimeValues: IDefaultUptimeValues = {
                no_operator_id: data?.no_operator_details[0]?.user_id,
                not_selected_type_id: data?.failure_type?.find(
                    (ft: IClusterFailureType) => ft.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
                )?.failure_type_id,
            };
            const clusterDetails: IClusterDetails = {
                mill_id: data.cluster[0].mill_id,
                cluster_id: data.cluster[0].cluster_id,
                name: data.cluster[0].name,
                cluster_code: data.cluster[0].cluster_code,
                user_cluster: data.cluster[0].user_clusters,
                dedicated_localserver: data?.cluster?.[0]?.mill?.dedicated_localserver,
            };

            parseClusterData(
                machineCentersCluster,
                clusterDetails,
                lastUpdated,
                mqttBrokerSettings,
                defaultUptimeValues,
                setStore,
            );
        } else {
            throw error;
        }
    } catch (error) {
        throw error;
    }
};
